import { GridRowId } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, RefObject, useCallback, useMemo } from "react";

import { useUpdateHotelClinicTasksMutation } from "@/requests/hotel-clinic.mutations";
import {
    ClinicTasksEditableFields,
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksGridChanges,
    useClinicArrivalTasksGridSaveConfirmOpen,
} from "@/stores/use-clinic-arrival-tasks-grid";
import { useHotelClinicArrivalDate } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

type ApiRefLike = MutableRefObject<GridApiPremium | null> | RefObject<GridApiPremium | null>;

function batchPickUpStatsFromChanges(changes: Record<GridRowId, ClinicTasksEditableFields>) {
    let row = 0;
    let cell = 0;
    for (const id of Object.keys(changes)) {
        const ch = changes[id];
        if (!ch?.pickUp || String(ch.pickUp).trim() === "") continue;
        row += 1;
        cell += 1;
    }
    return {
        totals: { row, cell },
    };
}

export function useClinicArrivalTasksPickUpSaveAction(apiRef: ApiRefLike) {
    const changes = useClinicArrivalTasksGridChanges();
    const saveConfirmOpen = useClinicArrivalTasksGridSaveConfirmOpen();
    const { setSaveConfirmOpen } = useClinicArrivalTasksGridActions();
    const date = useHotelClinicArrivalDate();
    const isoDate = date.toISODate() ?? undefined;
    const { isPending } = useUpdateHotelClinicTasksMutation({ isoDate });

    const { totals } = useMemo(() => batchPickUpStatsFromChanges(changes), [changes]);

    const handleSaveChanges = useCallback(() => {
        if (saveConfirmOpen) return;
        if (isPending || !apiRef.current) return;
        if (!totals.row) return;
        setSaveConfirmOpen(true);
    }, [apiRef, isPending, saveConfirmOpen, setSaveConfirmOpen, totals.row]);

    const saveDisabled = totals.row === 0 || isPending;

    return {
        totals,
        handleSaveChanges,
        saveDisabled,
        saveConfirmOpen,
        isPending,
    };
}
